import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import { getAllSignatures, UserKeys } from '../../../api/users-api';
import HtmlPreview from '../../../shared/html-preview';
import { Messages } from '../../../utils/message-constants';
import z from 'zod';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface ApplySignatureTemplateActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const ApplySignatureTemplateActionSchema = z.object({
  type: z.literal('Apply_Signature_Template'),
  signatureTemplateId: z.number().min(1, 'A signature template is required.'),
});

export type ApplySignatureTemplateActionType = z.infer<
  typeof ApplySignatureTemplateActionSchema
>;

// Type guard to check if an action is ApplySignatureTemplateActionType
function isApplySignatureTemplateAction(
  action: PolicyAction | undefined
): action is ApplySignatureTemplateActionType {
  return action?.type === 'Apply_Signature_Template';
}

export default function ApplySignatureTemplateAction({
  setValue,
  watch,
  errors,
  control,
}: ApplySignatureTemplateActionProps) {
  const { data: signatures = [] } = useQuery({
    queryKey: [UserKeys.Signatures],
    queryFn: () => getAllSignatures(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('signature templates'),
    },
  });

  const actionData = watch('actionData') ?? [];
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Apply_Signature_Template');
  const actionAtIndex = actionData[idx];
  if (!isApplySignatureTemplateAction(actionAtIndex)) {
    return null;
  }

  const currentAction: ApplySignatureTemplateActionType = actionAtIndex;
  const error: FieldErrors<ApplySignatureTemplateActionType> | undefined =
    errors?.actionData?.[idx];

  const selectedTemplate = signatures.find(
    (signature) => signature.id === currentAction.signatureTemplateId
  );

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Box sx={{ width: 400 }}>
          <Controller
            name={`actionData.${idx}.signatureTemplateId`}
            control={control}
            render={() => (
              <Autocomplete
                fullWidth
                autoComplete
                autoHighlight
                options={signatures}
                getOptionLabel={(option) => option.templateName || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  signatures.find(
                    (signature) =>
                      signature.id === currentAction.signatureTemplateId
                  ) || null
                }
                onChange={(_, value) =>
                  setValue(
                    `actionData.${idx}.signatureTemplateId`,
                    value ? value.id : 0,
                    { shouldDirty: true, shouldValidate: true }
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Signature Template"
                    error={!!error?.signatureTemplateId}
                    helperText={error?.signatureTemplateId?.message || ' '}
                  />
                )}
              />
            )}
          />
        </Box>
        {selectedTemplate && (
          <HtmlPreview
            html={selectedTemplate.templateHtml}
            title="Template Preview"
            minHeight={1}
          />
        )}
      </Stack>
    </Stack>
  );
}
